/**
 * @file ShipmentTrackEditor.tsx
 * @description Ввод и правка трек-номера отгрузки вручную (phase19.15 шаг 4).
 *
 * Зачем: у перевозчиков без интеграции (Почта, Яндекс, ПЭК, Ozon, 5Post,
 * «другой») трек-номер неоткуда взять автоматически, а на нём держатся и
 * контроль доставки, и ЛК клиента. У СДЭК трек приезжает сам — компонент там
 * не показывается вовсе (решение принимает карточка отгрузки).
 *
 * Пустую строку сервер не принимает: «стереть трек» — не сценарий, ошибочный
 * номер исправляют новым.
 */

import { Check, Pencil, X } from "lucide-react";
import { useState } from "react";

import { Button } from "../ui/button";
import { Input } from "../ui/input";

interface Props {
  trackNumber: string | null;
  /** Идёт другое действие над отгрузками — блокируем поле. */
  acting: boolean;
  /** Сохранение трека; тост и перечитывание списка — на стороне вызывающего. */
  onSave: (trackNumber: string) => void;
}

export function ShipmentTrackEditor({ trackNumber, acting, onSave }: Props) {
  const [editing, setEditing] = useState(false);
  const [value, setValue] = useState(trackNumber ?? "");

  function submit(): void {
    const next = value.trim();
    if (!next || next === trackNumber) {
      setEditing(false);
      setValue(trackNumber ?? "");
      return;
    }
    onSave(next.slice(0, 64));
    setEditing(false);
  }

  if (!editing) {
    return (
      <Button
        size="sm"
        variant="outline"
        className="min-h-11"
        disabled={acting}
        onClick={() => {
          setValue(trackNumber ?? "");
          setEditing(true);
        }}
      >
        <Pencil className="mr-1 size-4" />
        {trackNumber ? "Изменить трек" : "Указать трек"}
      </Button>
    );
  }

  return (
    <div className="flex w-full items-center gap-1 sm:w-auto">
      <Input
        className="min-h-11 w-full sm:w-56"
        value={value}
        maxLength={64}
        autoFocus
        aria-label="Трек-номер"
        placeholder="Номер у перевозчика"
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") submit();
          if (e.key === "Escape") setEditing(false);
        }}
      />
      <Button
        size="sm"
        className="size-11 shrink-0"
        aria-label="Сохранить трек-номер"
        disabled={acting}
        onClick={submit}
      >
        <Check className="size-4" />
      </Button>
      <Button
        size="sm"
        variant="ghost"
        className="size-11 shrink-0"
        aria-label="Отменить"
        onClick={() => setEditing(false)}
      >
        <X className="size-4" />
      </Button>
    </div>
  );
}
